/**
 * Gravity Forms — retire l'erreur d'un champ dès qu'il est corrigé.
 *
 * Ex. : champ obligatoire en erreur → l'utilisateur tape du texte →
 * le message rouge et la classe d'erreur disparaissent immédiatement.
 */

const FIELD_ERROR_SELECTOR = '.gfield_error, .gfield--error';
const FIELD_MESSAGE_SELECTOR = '.gfield_validation_message, .validation_message';
const FORM_ERRORS_SELECTOR = '.gform_validation_errors';

/**
 * @param {HTMLElement} field
 * @returns {boolean}
 */
function fieldHasValue(field) {
    const radios = field.querySelectorAll('input[type="radio"]');
    if (radios.length) {
        return Array.from(radios).some((input) => input.checked);
    }

    const checkboxes = field.querySelectorAll('input[type="checkbox"]');
    if (checkboxes.length) {
        return Array.from(checkboxes).some((input) => input.checked);
    }

    const fileInputs = field.querySelectorAll('input[type="file"]');
    if (fileInputs.length) {
        const hasNativeFile = Array.from(fileInputs).some(
            (input) => input.files && input.files.length > 0
        );
        const hasMultiPreview = field.querySelector(
            '.gform_fileupload_rules + ul li, .ginput_preview, .gform_preview_list li, [id*="gform_preview"] li'
        );
        return hasNativeFile || Boolean(hasMultiPreview);
    }

    const controls = field.querySelectorAll(
        'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="radio"]):not([type="checkbox"]):not([type="file"]), textarea, select'
    );

    if (!controls.length) {
        return false;
    }

    return Array.from(controls).some((control) => {
        const value = typeof control.value === 'string' ? control.value.trim() : '';
        return value !== '';
    });
}

/**
 * @param {HTMLElement} field
 */
function clearFieldError(field) {
    field.classList.remove('gfield_error', 'gfield--error');
    field.querySelectorAll(FIELD_MESSAGE_SELECTOR).forEach((message) => {
        message.remove();
    });

    field.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
        el.setAttribute('aria-invalid', 'false');
    });

    refreshFormErrorSummary(field.closest('form') || field.closest('.gform_wrapper'));
}

/**
 * Met à jour / masque le bandeau d'erreurs en tête de formulaire.
 *
 * @param {ParentNode|null} scope
 */
function refreshFormErrorSummary(scope) {
    if (!scope) {
        return;
    }

    const summary = scope.querySelector(FORM_ERRORS_SELECTOR);
    if (!summary) {
        return;
    }

    const remaining = scope.querySelectorAll(FIELD_ERROR_SELECTOR);
    if (!remaining.length) {
        summary.remove();
        return;
    }

    // Retire les liens du sommaire qui pointent vers des champs déjà corrigés.
    summary.querySelectorAll('a[href^="#field_"], a[href*="#field_"]').forEach((link) => {
        const href = link.getAttribute('href') || '';
        const id = href.includes('#') ? href.split('#').pop() : '';
        if (!id) {
            return;
        }

        const target = document.getElementById(id);
        if (!target || !target.matches(FIELD_ERROR_SELECTOR)) {
            const item = link.closest('li');
            if (item) {
                item.remove();
            } else {
                link.remove();
            }
        }
    });

    if (!summary.querySelector('li, a[href*="#field_"]')) {
        summary.remove();
    }
}

/**
 * @param {EventTarget|null} target
 * @returns {HTMLElement|null}
 */
function findErrorField(target) {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest(FIELD_ERROR_SELECTOR);
}

/**
 * @param {Event} event
 */
function onFieldInteraction(event) {
    const field = findErrorField(event.target);
    if (!field) {
        return;
    }

    if (fieldHasValue(field)) {
        clearFieldError(field);
    }
}

/**
 * Initialise l'écoute (délégation, compatible multipage / AJAX GF).
 */
export function initGfClearFieldErrors() {
    if (document.body.dataset.hfGfClearErrors === '1') {
        return;
    }

    document.body.dataset.hfGfClearErrors = '1';

    document.addEventListener('input', onFieldInteraction, true);
    document.addEventListener('change', onFieldInteraction, true);
}
